import { useState } from 'react';
import { Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import type { ProjectTypeProfile } from '../../../core/types/data-models';
import { DescriptionStep } from '../shared/DescriptionStep';
import { SourcesStep, isSourcesStepValid, isValidHttpUrl, normalizeUrl } from '../shared/SourcesStep';
import type { OnboardingDraft } from '../shared/useOnboardingDraft';
import { businessValidationCopy as copy } from './copy';

export function isCompanySourcesStepValid(draft: OnboardingDraft, profile: ProjectTypeProfile): boolean {
  return isSourcesStepValid(draft, profile) && draft.otherUrls.every(isValidHttpUrl);
}

interface CompanySourcesStepProps {
  draft: OnboardingDraft;
  onChange: (patch: Partial<OnboardingDraft>) => void;
  profile: ProjectTypeProfile;
}

/**
 * The one screen of the business-validation wizard: the company's public
 * sources (the profile's URL fields, then any further pages) and the
 * investor's optional focus points. Everything here ends up in the source
 * analysis that writes the company brief.
 */
export function CompanySourcesStep({ draft, onChange, profile }: CompanySourcesStepProps) {
  return (
    <Stack spacing={4}>
      <SourcesStep draft={draft} onChange={onChange} profile={profile} heading={copy.sources.heading} helper={copy.sources.helper} />
      <OtherUrlsField urls={draft.otherUrls} onChange={(otherUrls) => onChange({ otherUrls })} />
      <DescriptionStep
        value={draft.description}
        onChange={(description) => onChange({ description })}
        heading={copy.focus.heading}
        helper={copy.focus.helper}
        label={copy.focus.label}
        placeholder={copy.focus.placeholder}
        autoFocus={false}
      />
    </Stack>
  );
}

/** A list of further URLs: type one, add it, remove it again from its chip. */
function OtherUrlsField({ urls, onChange }: { urls: string[]; onChange: (urls: string[]) => void }) {
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = () => {
    const url = normalizeUrl(input);
    if (!url) return;
    if (!isValidHttpUrl(url)) {
      setError(copy.otherUrls.invalid);
      return;
    }
    if (!urls.includes(url)) onChange([...urls, url]);
    setInput('');
    setError(null);
  };

  return (
    <Box>
      <Typography variant="h6" component="h2" fontWeight={700}>
        {copy.otherUrls.heading}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        {copy.otherUrls.helper}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
        <TextField
          label={copy.otherUrls.label}
          type="url"
          inputMode="url"
          autoComplete="url"
          placeholder="https://"
          fullWidth
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            // Enter here adds the address; it must not submit the wizard.
            if (e.key !== 'Enter') return;
            e.preventDefault();
            add();
          }}
          error={error !== null}
          helperText={error ?? ' '}
        />
        <Button
          type="button"
          variant="outlined"
          startIcon={<AddIcon />}
          disabled={!input.trim()}
          onClick={add}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap', mt: { sm: '2px' } }}
        >
          {copy.otherUrls.add}
        </Button>
      </Stack>
      {urls.length > 0 && (
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mt: 0.5 }}>
          {urls.map((url) => (
            <Chip
              key={url}
              label={url}
              variant="outlined"
              onDelete={() => onChange(urls.filter((u) => u !== url))}
              sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
